import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaUser, FaClock, FaFileExcel, FaPlus, FaComments, FaTag, FaCheckCircle, FaHourglassHalf } from 'react-icons/fa';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { db } from '../../config/firebase';

function QA() {
  const [questions, setQuestions] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'solved', 'pending'

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const q = query(collection(db, 'questions'), orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);
        const questionsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setQuestions(questionsData);
      } catch (error) {
        console.error('Sorular yüklenirken bir hata oluştu:', error);
      }
    };

    fetchQuestions();
  }, []);

  const filteredQuestions = questions.filter(question => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'solved') return question.status === 'solved';
    if (statusFilter === 'pending') return question.status !== 'solved';
    return true;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      <div className="container mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <motion.h1 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-green-600 to-blue-600 text-transparent bg-clip-text"
          >
            Excel Forum
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto"
          >
            Excel ve VBA ile ilgili sorularınızı sorun, uzman cevaplarına ulaşın
          </motion.p>
        </motion.div>

        <div className="flex justify-between items-center mb-8">
          <div className="flex space-x-4">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-4 py-2 rounded-full ${
                statusFilter === 'all'
                  ? 'bg-green-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-green-50'
              }`}
            >
              Tümü
            </button>
            <button
              onClick={() => setStatusFilter('solved')}
              className={`px-4 py-2 rounded-full flex items-center ${
                statusFilter === 'solved'
                  ? 'bg-green-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-green-50'
              }`}
            >
              <FaCheckCircle className="mr-2" />
              Çözülenler
            </button>
            <button
              onClick={() => setStatusFilter('pending')}
              className={`px-4 py-2 rounded-full flex items-center ${
                statusFilter === 'pending'
                  ? 'bg-green-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-green-50'
              }`}
            >
              <FaHourglassHalf className="mr-2" />
              Bekleyenler
            </button>
          </div>

          <Link 
            to="/qa/new"
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-green-500 to-blue-500 text-white rounded-full shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200"
          >
            <FaPlus className="mr-2" />
            Yeni Soru Sor
          </Link>
        </div>

        <div className="grid grid-cols-1 gap-6">
          {filteredQuestions.map((question, index) => (
            <motion.div
              key={question.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
              className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300"
            >
              <Link to={`/qa/${question.id}`} className="block p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-r from-green-400 to-blue-400 flex items-center justify-center text-white">
                        <FaUser className="w-5 h-5" />
                      </div>
                      <span className="font-medium text-gray-700">{question.author}</span>
                    </div>
                    <div className="flex items-center text-gray-500">
                      <FaClock className="w-4 h-4 mr-1" />
                      <span className="text-sm">
                        {new Date(question.createdAt.seconds * 1000).toLocaleDateString('tr-TR')}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    {question.files?.length > 0 && (
                      <span className="flex items-center text-gray-500">
                        <FaFileExcel className="w-4 h-4 mr-1" />
                        <span className="text-sm">{question.files.length}</span>
                      </span>
                    )}
                    <span className="flex items-center text-gray-500">
                      <FaComments className="w-4 h-4 mr-1" />
                      <span className="text-sm">{question.answers?.length || 0}</span>
                    </span>
                  </div>
                  <span className={`flex items-center px-3 py-1 rounded-full text-sm ${
                    question.status === 'solved' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {question.status === 'solved' ? (
                      <>
                        <FaCheckCircle className="mr-2" />
                        Çözüldü
                      </>
                    ) : (
                      <>
                        <FaHourglassHalf className="mr-2" />
                        Beklemede
                      </>
                    )}
                  </span>
                </div>

                <h3 className="text-xl font-bold text-gray-800 mb-3 hover:text-green-600 transition-colors">
                  {question.question}
                </h3>

                {question.tags && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {question.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gradient-to-r from-green-100 to-blue-100 text-green-700"
                      >
                        <FaTag className="w-3 h-3 mr-1" />
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default QA; 